import React from "react";
import {
  Box,
  Container,
  Link,
  Typography,
  Link as MuiLink,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { CompanyInfo, companyInfoData } from "../../services/apiService";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Facebook as FacebookIcon,
  /*  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon, */
  Instagram as InstagramIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import GoogleRatingBadge from "../ui/GoogleRatingBadge";
import { useGoogleRating } from "../../hooks/useGoogleRating";

interface FooterProps {
  companyInfo: CompanyInfo;
}

const Footer: React.FC<FooterProps> = ({ companyInfo }) => {
  const currentYear = new Date().getFullYear();
  const { data: googleRating } = useGoogleRating();

  const socialLinks = [
    {
      icon: <FacebookIcon />,
      url: process.env.REACT_APP_PUBLIC_FACEBOOK_PAGE || "",
      label: "Facebook",
    },
    /* {
        icon: <TwitterIcon />,
        url: process.env.REACT_APP_PUBLIC_TWITTER_USERNAME || "",
        label: "Twitter",
      },
      {
        icon: <LinkedInIcon />,
        url: process.env.REACT_APP_PUBLIC_LINKEDIN_COMPANY || "",
        label: "LinkedIn",
      }, */
    {
      icon: <InstagramIcon />,
      url: process.env.REACT_APP_PUBLIC_INSTAGRAM_USERNAME || "",
      label: "Instagram",
    },
  ];

  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`,
        py: 6,
        mt: "auto",
      }}
    >
      <Container maxWidth="lg">
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          }}
          gap={4}
        >
          {/* Company Info */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                R.S. Enterprises
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Streamline your sales with powerful billing solutions and
              point-of-sale systems you can trust.
            </Typography>

            {/* Social Media */}
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              {socialLinks.map((link, index) => (
                <IconButton
                  key={index}
                  color="primary"
                  href={link.url}
                  aria-label={link.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.icon}
                </IconButton>
              ))}
            </Box>

            {/* Live Google rating (hidden until the API is configured) */}
            <GoogleRatingBadge data={googleRating} tone="light" />
          </Box>

          {/* Quick Links */}
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Quick Links
            </Typography>
            <Stack spacing={1}>
              <MuiLink
                component={RouterLink}
                to="/#services"
                color="text.secondary"
                underline="hover"
              >
                Services
              </MuiLink>
              {/* <MuiLink href="#solutions" color="text.secondary" underline="hover">Solutions</MuiLink> */}
              <MuiLink href="/" color="text.secondary" underline="hover">
                About Us
              </MuiLink>
              {/* <MuiLink href="#testimonials" color="text.secondary" underline="hover">Testimonials</MuiLink> */}
            </Stack>
          </Box>

          {/* Resources */}
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Resources
            </Typography>
            <Stack spacing={1}>
              <MuiLink
                component={RouterLink}
                to="/contact#contact-form"
                color="text.secondary"
                underline="hover"
              >
                Contact Us
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/blog"
                color="text.secondary"
                underline="hover"
              >
                Blog & Guides
              </MuiLink>
            </Stack>
          </Box>

          {/* Contact Info */}
          <Box id="contact">
            <Typography
              variant="h6"
              component="h3"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Contact Info
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <LocationOnIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {companyInfo.contact.address}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                <Link
                  href={`tel:${companyInfo.contact.redirectPhone}`}
                  color="text.secondary"
                  underline="hover"
                >
                  {companyInfo.contact.redirectPhone}
                </Link>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <EmailIcon color="primary" sx={{ mr: 1 }} />
                <Link
                  href={`mailto:${companyInfo.contact.redirectEmail}`}
                  color="text.secondary"
                  underline="hover"
                >
                  {companyInfo.contact.redirectEmail}
                </Link>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* Footer Bottom */}
      <Container maxWidth="lg">
        <Box
          sx={{
            pt: 4,
            mt: 4,
            borderTop: `1px solid ${theme.palette.divider}`,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            &copy; {currentYear} R.S. Enterprises. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// Floating action buttons component
interface FloatingActionButtonsProps {
  companyInfo: CompanyInfo;
}

const FloatingActionButtons: React.FC<FloatingActionButtonsProps> = ({
  companyInfo,
}) => {
  const theme = useTheme();

  const handleWhatsAppClick = () => {
    const phoneNumber =
      companyInfo.contact.whatsapp || companyInfo.contact.phone;
    const defaultMessage = encodeURIComponent(
      "Hello! I'm interested in your products/services. Could you please provide more information?",
    );
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, "")}?text=${defaultMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleCallClick = () => {
    const phoneNumber = companyInfo.contact.redirectPhone;
    window.location.href = `tel:${phoneNumber}`;
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 80, // Fixed position above scroll-to-top button
        right: 20,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        zIndex: 999, // Just below scroll-to-top button
        "& > *": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          transition: "transform 0.2s ease-in-out",
          "&:active": {
            transform: "scale(0.95)",
          },
        },
        // Adjust for larger screens
        "@media (min-width: 600px)": {
          bottom: 24,
          right: 100, // Position to the left of scroll-to-top button
          "&:hover > *": {
            transform: "translateX(-4px)",
          },
        },
        // Ensure proper spacing on very small devices
        "@media (max-width: 400px)": {
          bottom: 70,
          right: 16,
          gap: "12px",
        },
      }}
    >
      {/* WhatsApp Button */}
      <IconButton
        onClick={handleWhatsAppClick}
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: "#25D366",
          boxShadow: theme.shadows[6],
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: theme.shadows[10],
            bgcolor: "#25D366",
          },
        }}
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon sx={{ color: "white", fontSize: 32 }} />
      </IconButton>

      {/* Call Button */}
      <IconButton
        onClick={handleCallClick}
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: theme.palette.secondary.main,
          boxShadow: theme.shadows[6],
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: theme.shadows[10],
            bgcolor: theme.palette.secondary.main,
          },
        }}
        aria-label="Call us"
      >
        <CallIcon sx={{ color: "white", fontSize: 28 }} />
      </IconButton>
    </Box>
  );
};

const FooterWithButtons = () => {
  // Static data — synchronous for prerender/hydration parity
  const companyInfo = companyInfoData;

  return (
    <>
      <Footer companyInfo={companyInfo} />
      <FloatingActionButtons companyInfo={companyInfo} />
    </>
  );
};

export default FooterWithButtons;
